import React, { HTMLAttributes, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Immutable from 'immutable';

import { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import {
  Container,
} from '@uncinc/uncinc-react-kitchen-sink';
import { CrisisAlert } from '../../types';
import {
  CrisisBanner,
  Loader,
  CrisisBannerValidateEmail,
  Support,
} from '../../components';
import { DOMAIN_ZELFHULP_BEGELEID } from '../../config/constants';
import { getEmailDomain } from '../../helpers/email';
import { MenuTabbar } from '../../implementations';

import './style.scss';

interface Props extends React.DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  hideChrome?: boolean;
  hideCrisisBanner?: boolean;
}

const Layout: React.FC<Props> = ({
  children, className, hideChrome, hideCrisisBanner,
}) => {
  const isLoggedIn = useSelector<ImmutableRootState<boolean>, boolean>((state) => state.getIn(['login', 'loggedIn'], null));
  const currentUserId = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['login', 'uid'], null));
  const userHasValidatedEmail = useSelector<ImmutableRootState<boolean>, boolean>((state) => state.getIn(['user', currentUserId, 'field_validated_email', 0, 'value'], false));
  const currentDomain = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['uiInternal', 'currentDomain', 'raw'], ''));
  const userHasValidEmail = useSelector<ImmutableRootState<string>, boolean>((state) => {
    const userEmail = state.getIn(['user', currentUserId, 'mail', 0, 'value'], '');
    return getEmailDomain(userEmail) !== 'example.com';
  });

  const crisisAlerts = useSelector<ImmutableRootState<
  Immutable.List<Immutable.RecordOf<CrisisAlert>>>,
  Immutable.List<Immutable.RecordOf<CrisisAlert>>>(
    (state) => state.getIn(['uiInternal', 'crisis_alerts'], Immutable.List<Immutable.RecordOf<CrisisAlert>>()),
  );

  const showValidateEmailCrisisBanner = useMemo(() => (
    // eslint-disable-next-line max-len
    isLoggedIn && currentDomain !== DOMAIN_ZELFHULP_BEGELEID && !userHasValidatedEmail && userHasValidEmail
  ), [currentDomain, isLoggedIn, userHasValidatedEmail, userHasValidEmail]);

  const renderLayout = () => {
    switch (isLoggedIn) {
      case true:
        if (!hideChrome) {
          return (
            <Container>
              {(!hideCrisisBanner && (showValidateEmailCrisisBanner || crisisAlerts.size > 0)) && (
                <div className="row">
                  <div className="col-12 mt-4 mt-md-8 mt-lg-12">
                    <div className="crisis-alerts-wrapper">
                      {showValidateEmailCrisisBanner && (
                        <CrisisBannerValidateEmail />
                      )}

                      {crisisAlerts.map((alert) => (
                        <CrisisBanner
                          key={alert.get('nid')}
                          nid={alert.get('nid')}
                          type={alert.get('field_type')}
                          title={alert.get('title')}
                          body={alert.get('body')}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="row">
                <div className="col-lg-2 order-last order-md-first py-lg-12">
                  <MenuTabbar />
                </div>
                {children}
              </div>

              <Support />
            </Container>
          );
        }

        return children;

      case false:
        return children;

      default:
        return <Loader />;
    }
  };

  return (
    <div className={className}>
      {renderLayout()}
    </div>
  );
};

export default Layout;
